// usePoints — small hook that fetches and exposes the customer's points
// balance + per-region breakdown. Refetches when called via `refresh`.
// Intended for customer dashboard / cart / profile.

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useFlag } from '../context/FeatureFlagsContext';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export function usePoints() {
  const { user } = useAuth();
  const enabled = useFlag('customer.loyalty', true);
  const [balance, setBalance] = useState(0);
  const [byRegion, setByRegion] = useState({});
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!enabled || !user || user.role !== 'customer') {
      setBalance(0);
      setByRegion({});
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/users/me/points`, {
        withCredentials: true,
      });
      setBalance(Number(data?.balance || 0));
      setByRegion(data?.by_region || {});
    } catch (_) {
      setBalance(0);
      setByRegion({});
    } finally {
      setLoading(false);
    }
  }, [enabled, user?.id, user?.role]);

  useEffect(() => { refresh(); }, [refresh]);

  return { balance, byRegion, loading, refresh, enabled };
}
